import { useMemo, useRef, useEffect } from 'react';

function MiniEquityCurve({ data, width = 260, height = 80 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || data.length < 2) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const pad = 4;
    const w = width - pad * 2;
    const h = height - pad * 2;

    // Zero line
    const startVal = data[0];
    const zeroY = pad + h - ((startVal - min) / range) * h;
    ctx.strokeStyle = getComputedStyle(document.documentElement).getPropertyValue('--border-color').trim() || '#333';
    ctx.lineWidth = 0.5;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(pad, zeroY);
    ctx.lineTo(width - pad, zeroY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Equity line
    const lastVal = data[data.length - 1];
    const color = lastVal >= startVal ? '#22c55e' : '#ef4444';
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    for (let i = 0; i < data.length; i++) {
      const x = pad + (i / (data.length - 1)) * w;
      const y = pad + h - ((data[i] - min) / range) * h;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();

    // Fill
    ctx.lineTo(width - pad, pad + h);
    ctx.lineTo(pad, pad + h);
    ctx.closePath();
    ctx.fillStyle = lastVal >= startVal ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)';
    ctx.fill();
  }, [data, width, height]);

  return <canvas ref={canvasRef} style={{ width, height, display: 'block' }} />;
}

export default function PerformanceDashboard({ startingCapital, currentCapital, equity, closedTrades, openPositions }) {
  const stats = useMemo(() => {
    const trades = closedTrades || [];
    const total = trades.length;
    if (total === 0) return null;

    const wins = trades.filter(t => t.pnl > 0);
    const losses = trades.filter(t => t.pnl <= 0);
    const winRate = (wins.length / total) * 100;
    const avgWin = wins.length > 0 ? wins.reduce((s, t) => s + t.pnl, 0) / wins.length : 0;
    const avgLoss = losses.length > 0 ? Math.abs(losses.reduce((s, t) => s + t.pnl, 0) / losses.length) : 0;
    const profitFactor = avgLoss > 0 ? (wins.reduce((s, t) => s + t.pnl, 0)) / Math.abs(losses.reduce((s, t) => s + t.pnl, 0) || 1) : Infinity;
    const expectancy = total > 0 ? trades.reduce((s, t) => s + t.pnl, 0) / total : 0;
    const totalPnl = trades.reduce((s, t) => s + t.pnl, 0);
    const returnPct = startingCapital > 0 ? ((equity - startingCapital) / startingCapital) * 100 : 0;

    // Max drawdown from equity curve
    let peak = startingCapital;
    let maxDD = 0;
    let running = startingCapital;
    for (const t of trades) {
      running += t.pnl;
      if (running > peak) peak = running;
      const dd = peak - running;
      if (dd > maxDD) maxDD = dd;
    }
    const maxDDPct = peak > 0 ? (maxDD / peak) * 100 : 0;

    // Sharpe (simplified: daily PnL std dev)
    const pnls = trades.map(t => t.pnl);
    const mean = pnls.reduce((a, b) => a + b, 0) / pnls.length;
    const variance = pnls.reduce((s, p) => s + (p - mean) ** 2, 0) / pnls.length;
    const stdDev = Math.sqrt(variance);
    const sharpe = stdDev > 0 ? mean / stdDev : 0;

    // Equity curve points
    const equityCurve = [startingCapital];
    let eq = startingCapital;
    for (const t of trades) {
      eq += t.pnl;
      equityCurve.push(Math.round(eq * 100) / 100);
    }

    // Consecutive wins/losses
    let maxConsecWins = 0, maxConsecLosses = 0, cw = 0, cl = 0;
    for (const t of trades) {
      if (t.pnl > 0) { cw++; cl = 0; if (cw > maxConsecWins) maxConsecWins = cw; }
      else { cl++; cw = 0; if (cl > maxConsecLosses) maxConsecLosses = cl; }
    }

    return {
      total, winRate, avgWin, avgLoss, profitFactor, expectancy, totalPnl,
      returnPct, maxDD, maxDDPct, sharpe, equityCurve, maxConsecWins, maxConsecLosses,
      wins: wins.length, losses: losses.length,
    };
  }, [closedTrades, startingCapital, equity]);

  if (!stats) {
    return (
      <div className="perf-dashboard">
        <div className="perf-header">Performance</div>
        <div className="empty-state" style={{ padding: '1rem' }}>No closed trades yet</div>
      </div>
    );
  }

  const pnlClass = stats.totalPnl >= 0 ? 'pnl-positive' : 'pnl-negative';

  return (
    <div className="perf-dashboard">
      <div className="perf-header">Performance</div>
      <MiniEquityCurve data={stats.equityCurve} />
      <div className="perf-grid">
        <div className="perf-stat">
          <span className="perf-label">Return</span>
          <span className={`perf-value ${pnlClass}`}>{stats.returnPct >= 0 ? '+' : ''}{stats.returnPct.toFixed(2)}%</span>
        </div>
        <div className="perf-stat">
          <span className="perf-label">Net P&L</span>
          <span className={`perf-value ${pnlClass}`}>{stats.totalPnl >= 0 ? '+' : ''}{stats.totalPnl.toFixed(2)}</span>
        </div>
        <div className="perf-stat">
          <span className="perf-label">Trades</span>
          <span className="perf-value">{stats.total} ({stats.wins}W / {stats.losses}L)</span>
        </div>
        <div className="perf-stat">
          <span className="perf-label">Win Rate</span>
          <span className="perf-value">{stats.winRate.toFixed(1)}%</span>
        </div>
        <div className="perf-stat">
          <span className="perf-label">Profit Factor</span>
          <span className="perf-value">{stats.profitFactor === Infinity ? '∞' : stats.profitFactor.toFixed(2)}</span>
        </div>
        <div className="perf-stat">
          <span className="perf-label">Expectancy</span>
          <span className={`perf-value ${stats.expectancy >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>{stats.expectancy.toFixed(2)}</span>
        </div>
        <div className="perf-stat">
          <span className="perf-label">Avg Win</span>
          <span className="perf-value pnl-positive">+{stats.avgWin.toFixed(2)}</span>
        </div>
        <div className="perf-stat">
          <span className="perf-label">Avg Loss</span>
          <span className="perf-value pnl-negative">-{stats.avgLoss.toFixed(2)}</span>
        </div>
        <div className="perf-stat">
          <span className="perf-label">Max DD</span>
          <span className="perf-value pnl-negative">-{stats.maxDD.toFixed(2)} ({stats.maxDDPct.toFixed(1)}%)</span>
        </div>
        <div className="perf-stat">
          <span className="perf-label">Sharpe</span>
          <span className="perf-value">{stats.sharpe.toFixed(2)}</span>
        </div>
        <div className="perf-stat">
          <span className="perf-label">Max Consec. Wins</span>
          <span className="perf-value">{stats.maxConsecWins}</span>
        </div>
        <div className="perf-stat">
          <span className="perf-label">Max Consec. Losses</span>
          <span className="perf-value">{stats.maxConsecLosses}</span>
        </div>
      </div>
    </div>
  );
}
